// QueryProvider.tsx
import React from 'react';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import {useSnackbar} from './SnackbarContextProvider';
import {getAxiosErrorMessage} from '../services/axiosInstance';
import {setQueryClient} from '../utils/queryClientRegistry';

export default function QueryProvider({children}: {children: React.ReactNode}) {
  const {showSnackbar} = useSnackbar();

  const clientRef = React.useRef<QueryClient>(null);
  if (!clientRef.current) {
    clientRef.current = new QueryClient({
      queryCache: new QueryCache({
        onError: (error, query) => {
          const retries = (query.options.retry ?? 0) as number;
          const failureCount = query.state.fetchFailureCount ?? 0;
          const isFinalAttempt = failureCount >= retries;
          const wantsToast = query.meta?.toastOnError !== false;
          if (wantsToast && isFinalAttempt) {
            showSnackbar({message: getAxiosErrorMessage(error), type: 'error'});
          }
        },
      }),
      mutationCache: new MutationCache({
        onError: (error, _vars, _ctx, mutation) => {
          const retries = (mutation.options.retry ?? 0) as number;
          const failureCount = mutation.state.failureCount ?? 0;
          const isFinalAttempt = failureCount >= retries;
          const wantsToast = mutation.options.meta?.toastOnError !== false;
          if (wantsToast && isFinalAttempt) {
            showSnackbar({message: getAxiosErrorMessage(error), type: 'error'});
          }
        },
      }),
      defaultOptions: {
        queries: {
          staleTime: 1 * 60_000,
          gcTime: 5 * 60_000,
          refetchOnWindowFocus: false,
          refetchOnReconnect: true,
          refetchOnMount: true,
          retry: 2,
        },
        mutations: {retry: 0},
      },
    });

    setQueryClient(clientRef.current);
  }

  return (
    <QueryClientProvider client={clientRef.current}>
      {children}
    </QueryClientProvider>
  );
}
